import { Component, Prop, h } from '@stencil/core';
import { store } from '@stencil/redux';
import { selectPaymentSplitOption, initPayment, initForOrder } from '../../../redux/actions';
import { PaymentConfig, SplitOption } from '../../../redux/types';
import { t, formatPrice } from '../../../redux/utils';
import { Price, SplitOptionType } from '../../../types/types';
import { PaymentOption } from '../utils/types';

@Component({
  tag: 'rtr-payment-split',
  styleUrl: '../style.sass',
  shadow: false,
})
export class RtrPaymentSplit {
  @Prop() orderReference: string;
  @Prop() selectedMethod: string;
  @Prop() paymentConfig: PaymentConfig;
  @Prop() selectedSplitOption: SplitOption;
  @Prop() depositPrice: Price;
  @Prop() setWaiting: () => void;
  @Prop() releaseWaiting: () => void;

  selectPaymentSplitOption!: typeof selectPaymentSplitOption;
  initForOrder!: typeof initForOrder;
  initPayment!: typeof initPayment;

  componentWillLoad() {
    store.mapDispatchToProps(this, {
      initPayment,
      initForOrder,
      selectPaymentSplitOption,
    });
  }

  componentDidLoad() {
      const splitOptions = this.splitOptions()
      if (splitOptions.length > 0) {
        this.handleSplitOptionChange(splitOptions[0]);
      }

  }

  componentDidUpdate() {
    const defaultSplitOption = this.splitOptions()[0];
    if (defaultSplitOption) {
      // if something selected but not valid in current context then overwrite to default
      if (this.selectedSplitOption) {
        if (!this.splitOptions().some(item => item.value === this.selectedSplitOption.value && item.type === this.selectedSplitOption.type)) {
          this.handleSplitOptionChange(defaultSplitOption);
        }
      } else {
        this.handleSplitOptionChange(defaultSplitOption);
      }
    }
  }

  splitOptions = (): any[] => {
    const splitOptions = this.paymentConfig?.splitOptions;
    if (!splitOptions) {
      return [];
    }

    let defaultOption = splitOptions.default;
    let optional = splitOptions.optional;

    const options = [];
    if (this.selectedMethod && splitOptions.byMethod?.length > 0) {
      const byMethodSplit = splitOptions.byMethod.find(
        item => item.paymentMethod === this.selectedMethod || (item.paymentMethod === 'sepa-direct-debit-stripe' && this.selectedMethod === PaymentOption.StripeSepa),
      );
      if (byMethodSplit) {
        defaultOption = byMethodSplit.default;
        optional = byMethodSplit.optional;
      }
    }

    const translateLabel = (option: SplitOption): string => {
      switch (option.type) {
        case SplitOptionType.Amount:
          return t('payment.prepaymentAmount', {
            amount: formatPrice(option.value * 100),
          });
        case SplitOptionType.Percentage:
            let key =  'payment.prepayment'
            if (option.value === 100) {
                key = option.excludeDeposit ? 'payment.prepaymentFullWithoutDeposit' : 'payment.prepaymentFull'
            }

          return t(key, { percentage: option.value });
        case SplitOptionType.Deposit:
          const depositPrice = this.depositPrice;
          const amount = formatPrice(depositPrice.gross);

          return t('payment.prepaymentDeposit', { amount: amount });
        default:
          return option.value + ' ' + option.type;
      }
    };

    if (defaultOption) {
      options.push({
        value: defaultOption.value,
        label: translateLabel(defaultOption),
        type: defaultOption.type,
        excludeDeposit: defaultOption.excludeDeposit,
        extraCartItemId: defaultOption.extraCartItemId,
      });
    }

    if (optional) {
      optional.forEach(item => {
        options.push({
          value: item.value,
          label: translateLabel(item),
          type: item.type,
          excludeDeposit: item.excludeDeposit,
          extraCartItemId: item.extraCartItemId,
        });
      });
    }

    return options;
  };

  mappedSplitOption = item => {
    return (
      <label class="RTPItemLabelContainer">
        <rentware-radio
          name="splitOption"
          label={item.label}
          value={item.value}
          checked={this.selectedSplitOption && this.selectedSplitOption.value === item.value && this.selectedSplitOption.type === item.type && this.selectedSplitOption.excludeDeposit === item.excludeDeposit}
          onChange={e => this.handleSplitOptionChange(item, e)}
        />
      </label>
    );
  };

  handleSplitOptionChange = (option: SplitOption, e?: any) => {
    e?.preventDefault();
    this.setWaiting();

    this.selectPaymentSplitOption({
      orderReference: this.orderReference,
      option: option,
      onSuccess: () => {
        this.initPayment({
          orderReference: this.orderReference,
          onSuccess: () => {
            this.initForOrder(this.releaseWaiting, this.orderReference);
          },
          onError: this.releaseWaiting,
        });
      },
      onError: this.releaseWaiting,
    });
  };

  render() {
    if (this.splitOptions().length <= 1 || !this.selectedSplitOption) {
      return null;
    }

    return (
      <div class="RTPOptionsContainer">
        <div class="RTPOptionsTitle">{t('payment.amountTitle')}</div>
        <rentware-spacer size="S" />

        {this.splitOptions().map(this.mappedSplitOption)}
      </div>
    );
  }
}
